import { EarningsFilterType, EarningsTableStatus, UserDeposit } from '../types';

/**
 * Filters deposits based on EarningsFilterType
 */
export const filterDeposits = (
  deposits: UserDeposit[],
  filter: EarningsFilterType
): UserDeposit[] => {
  switch (filter) {
    case EarningsFilterType.ACTIVE:
      return deposits.filter(
        (d) =>
          d.status === EarningsTableStatus.FLEXIBLE ||
          d.status === EarningsTableStatus.DAYS_LEFT ||
          d.status === EarningsTableStatus.FINISHED
      );
    case EarningsFilterType.CLAIMED:
      return deposits.filter(
        (d) =>
          d.status === EarningsTableStatus.CLAIMED ||
          d.status === EarningsTableStatus.WITHDRAWN
      );
    case EarningsFilterType.ALL:
    default:
      return deposits;
  }
};

/**
 * Sort priority for deposit status
 */
const getStatusPriority = (status: EarningsTableStatus): number => {
  switch (status) {
    case EarningsTableStatus.FINISHED: // Ready to be claimed
      return 0;
    case EarningsTableStatus.FLEXIBLE:
      return 1;
    case EarningsTableStatus.DAYS_LEFT:
      return 2;
    case EarningsTableStatus.CLAIMED:
      return 3;
    case EarningsTableStatus.WITHDRAWN:
      return 4;
    default:
      return 5;
  }
};

/**
 * Default sort for deposits:
 * 1. Ready to be claimed (FINISHED)
 * 2. Flexible
 * 3. Days left (ascending)
 * 4. Claimed/Withdrawn (by date)
 */
export const sortDepositsDefault = (deposits: UserDeposit[]): UserDeposit[] => {
  return [...deposits].sort((a, b) => {
    // First, sort by status priority
    const priorityDiff = getStatusPriority(a.status) - getStatusPriority(b.status);
    if (priorityDiff !== 0) return priorityDiff;

    // For DAYS_LEFT, sort by days remaining (ascending)
    if (a.status === EarningsTableStatus.DAYS_LEFT && b.status === EarningsTableStatus.DAYS_LEFT) {
      return (a.daysLeft ?? 0) - (b.daysLeft ?? 0);
    }

    // Fallback: sort by start date (newest first)
    return b.stakingStartDate.getTime() - a.stakingStartDate.getTime();
  });
};

/**
 * Sort deposits by a given key and direction
 */
export const sortDeposits = (
  deposits: UserDeposit[],
  sortConfig: { key: string; direction: 'asc' | 'desc' }
): UserDeposit[] => {
  const { key, direction } = sortConfig;
  const multiplier = direction === 'asc' ? 1 : -1;

  return [...deposits].sort((a, b) => {
    switch (key) {
      case 'phase':
        return multiplier * a.phaseName.localeCompare(b.phaseName);
      case 'stakedAmount':
        return multiplier * (a.amount - b.amount);
      case 'apy': {
        const apyA = parseFloat(a.apy.replace('%', '')) || 0;
        const apyB = parseFloat(b.apy.replace('%', '')) || 0;
        return multiplier * (apyA - apyB);
      }
      case 'reward':
        return multiplier * (a.rewards - b.rewards);
      default:
        return 0;
    }
  });
};
